"use strict";
// Copyright © Veeam Software Group GmbH
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WizardInteractor = void 0;
const core_1 = require("@veeam/core");
class WizardInteractor extends core_1.Interactor {
    constructor(router, listener, presenter, config = { isNextStepAvailable: true }) {
        super();
        this.router = router;
        this.listener = listener;
        this.presenter = presenter;
        this.config = config;
        this.loading = 0;
        this.pending = 0;
        this.stepRegistry = {};
        this.completedIndex = {};
        this.visitedIndex = {};
        this.stepsUnavailability = {};
        this.stepsDisableState = {};
        this.actionsState = {
            prev: {},
            next: {},
            skip: {},
            done: {},
            cancel: {}
        };
        this.currentStepId = '';
        this.presenter.listener = this;
    }
    tryCompleteSteps(ids) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.startLoading();
            try {
                const completedIds = [];
                let result = { ok: true, completedIds };
                for (const id of ids) {
                    const ok = yield ((_a = this.router) === null || _a === void 0 ? void 0 : _a.completeStepById(id));
                    if (!ok) {
                        result = {
                            completedIds,
                            ok: false,
                            firstFailedStepId: id
                        };
                        break;
                    }
                    this.completedIndex[id] = ok;
                    completedIds.push(id);
                }
                yield this.updateNavigation();
                return result;
            }
            finally {
                this.cancelLoading();
            }
        });
    }
    canLeaveCurrentStep() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.startLoading();
            try {
                return (_b = (yield ((_a = this.router) === null || _a === void 0 ? void 0 : _a.canLeaveCurrentStep()))) !== null && _b !== void 0 ? _b : false;
            }
            finally {
                this.cancelLoading();
            }
        });
    }
    isActionNextIsAvailable() {
        const { next } = this.getActionsState();
        return !next.disabled && !next.hidden;
    }
    isActionDoneIsAvailable() {
        const { done } = this.getActionsState();
        return !done.disabled && !done.hidden;
    }
    computeActionsState() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        return __awaiter(this, void 0, void 0, function* () {
            const { prev, next, skip, done, cancel } = this.actionsState;
            const canSkip = yield this.canSkip();
            let hasPrev = false;
            try {
                this.getNextOrPrevStepId(-1);
                hasPrev = true;
            }
            catch (_l) { }
            const result = {
                prev: {
                    hidden: (_a = (!hasPrev || prev.hidden)) !== null && _a !== void 0 ? _a : false,
                    disabled: prev.disabled,
                    title: prev.title
                },
                next: {
                    hidden: (_b = (!this.isNextAvailable() || next.hidden)) !== null && _b !== void 0 ? _b : false,
                    disabled: next.disabled,
                    title: next.title
                },
                skip: {
                    hidden: (_c = (!canSkip || skip.hidden)) !== null && _c !== void 0 ? _c : false,
                    disabled: skip.disabled,
                    title: skip.title
                },
                done: {
                    hidden: (_d = (!this.canDone() || done.hidden)) !== null && _d !== void 0 ? _d : false,
                    disabled: done.disabled,
                    title: done.title
                },
                cancel: {
                    hidden: (_e = (!this.canCancel() || cancel.hidden)) !== null && _e !== void 0 ? _e : false,
                    disabled: cancel.disabled,
                    title: cancel.title
                }
            };
            if (this.config.prioritizeUseActionsState) {
                result.prev.hidden = (_f = prev.hidden) !== null && _f !== void 0 ? _f : !hasPrev;
                result.next.hidden = (_g = next.hidden) !== null && _g !== void 0 ? _g : !this.isNextAvailable();
                result.skip.hidden = (_h = skip.hidden) !== null && _h !== void 0 ? _h : !canSkip;
                result.done.hidden = (_j = done.hidden) !== null && _j !== void 0 ? _j : !this.canDone();
                result.cancel.hidden = (_k = cancel.hidden) !== null && _k !== void 0 ? _k : !this.canCancel();
            }
            return result;
        });
    }
    updateNavigation() {
        return __awaiter(this, void 0, void 0, function* () {
            const navigation = yield this.getNavigation();
            const actions = yield this.computeActionsState();
            this.presenter.presentNavigation({
                navigation,
                actions
            });
        });
    }
    updatePendingState() {
        this.presenter.presentPending({ loading: this.isLoading(), pending: this.isPending() });
    }
    getNextOrPrevStepId(direction) {
        const stepIDs = this.getAvailableStepIDs();
        const nextIndex = this.getNextOrPrevStepIndex(direction);
        const nextId = stepIDs[nextIndex];
        if (!nextId) {
            throw new Error(`WizardInteractor error: step is out of range`);
        }
        return nextId;
    }
    canSkip() {
        return __awaiter(this, void 0, void 0, function* () {
            // const step = await this.router.getActionableStep(this.currentStep);
            // return step.canSkip();
            // TODO: implement
            return false;
        });
    }
    canDone(forceDone = true) {
        return this.isEnd() || (forceDone && this.isAllCompleted());
    }
    isEnd() {
        const stepIDs = this.getAvailableStepIDs();
        return stepIDs[stepIDs.length - 1] === this.currentStepId;
    }
    isNextAvailable() {
        if (this.isEnd()) {
            return false;
        }
        try {
            this.getNextOrPrevStepId(1);
            return true;
        }
        catch (_a) { }
        return false;
    }
    isAllCompleted() {
        const stepIDs = this.getNotDisabledAvailableStepIDs();
        return stepIDs.every((id) => this.completedIndex[id]);
    }
    getNavigation() {
        return __awaiter(this, void 0, void 0, function* () {
            const stepsIDs = this.getAvailableStepIDs();
            const currentStepId = this.currentStepId;
            const nextIndex = this.getNextOrPrevStepIndex(1);
            return stepsIDs.map((id, idx) => {
                const isNext = this.config.isNextStepAvailable && nextIndex === idx;
                const active = id === currentStepId;
                const lastStepId = stepsIDs[stepsIDs.length - 1];
                const isLastStep = lastStepId === id;
                const enable = idx === 0 ||
                    active ||
                    this.completedIndex[id] ||
                    isNext ||
                    (isLastStep &&
                        this.visitedIndex[lastStepId] &&
                        this.completedIndex[stepsIDs[idx - 1]]);
                // TODO: skip by isCompleted or isSkipped?
                return {
                    id,
                    disabled: this.stepsDisableState[id] || !enable,
                    active
                };
            });
        });
    }
    completeCurrentStep() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.startLoading();
                const result = (_b = (yield ((_a = this.router) === null || _a === void 0 ? void 0 : _a.completeStepById(this.currentStepId)))) !== null && _b !== void 0 ? _b : false;
                this.completedIndex[this.currentStepId] = result;
                return result;
            }
            catch (e) {
                this.internalDidCatchError(e);
            }
            finally {
                this.cancelLoading();
            }
            return false;
        });
    }
    internalDidCatchError(err) {
        if (typeof this.listener.didCatchError === 'function') {
            this.listener.didCatchError(err);
            return;
        }
        throw err;
    }
    getAvailableStepIDs() {
        return Object.values(this.stepRegistry)
            .sort((a, b) => a.index - b.index)
            .filter((step) => !this.stepsUnavailability[step.id])
            .map((value) => value.id);
    }
    getNotDisabledAvailableStepIDs() {
        return this.getAvailableStepIDs().filter((id) => !this.stepsDisableState[id]);
    }
    getNextOrPrevStepIndex(direction) {
        const stepIDs = this.getAvailableStepIDs();
        const notDisabledStepsIDs = this.getNotDisabledAvailableStepIDs();
        const currentStepId = this.currentStepId;
        const nextIndex = notDisabledStepsIDs.indexOf(currentStepId) + direction;
        return stepIDs.indexOf(notDisabledStepsIDs[nextIndex]);
    }
    getStartStepId() {
        const ids = this.getAvailableStepIDs();
        const id = ids[0];
        if (!id) {
            throw new Error('WizardInteractor error: no steps registered');
        }
        return ids[0];
    }
    canCancel() {
        var _a, _b;
        const currentStepId = this.currentStepId;
        return (_b = (_a = this.stepRegistry[currentStepId]) === null || _a === void 0 ? void 0 : _a.canCancel) !== null && _b !== void 0 ? _b : true;
    }
    willActivate() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield ((_a = this.router) === null || _a === void 0 ? void 0 : _a.attachSteps());
                yield this.switchStepById(this.getStartStepId());
                this.currentStepId = this.getStartStepId();
            }
            catch (e) {
                this.internalDidCatchError(e);
            }
        });
    }
    dropStepValidation(stepId) {
        return __awaiter(this, void 0, void 0, function* () {
            const step = this.stepRegistry[stepId];
            if (step) {
                this.completedIndex[step.id] = false;
                yield this.updateNavigation();
            }
            else {
                throw new Error('dropStepValidation: Cannot find the step with given ID');
            }
        });
    }
    dropAllNextStepsValidation(startStepId) {
        return __awaiter(this, void 0, void 0, function* () {
            const startStep = this.stepRegistry[startStepId];
            if (startStep) {
                const { index } = startStep;
                Object.values(this.stepRegistry).forEach((step) => {
                    if (step.index >= index) {
                        this.completedIndex[step.id] = false;
                    }
                });
                yield this.updateNavigation();
            }
            else {
                throw new Error('dropAllNextStepsValidation: Cannot find the step with given ID');
            }
        });
    }
    tryCompleteAllSteps() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.tryCompleteSteps(this.getNotDisabledAvailableStepIDs());
        });
    }
    didSelectStepById(nextStepId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const stepIDs = this.getNotDisabledAvailableStepIDs();
                const currentIndex = stepIDs.indexOf(this.currentStepId);
                const nextIndex = stepIDs.indexOf(nextStepId);
                const canLeave = yield this.canLeaveCurrentStep();
                if (!canLeave) {
                    return;
                }
                if (nextIndex < 0) {
                    throw new Error('WizardInteractor error: can not switch route, route not available or not exist');
                }
                if (nextIndex === currentIndex) {
                    return;
                }
                if (nextIndex > currentIndex) {
                    const stepIDsForValidate = [];
                    for (let currentValidateIndex = currentIndex; currentValidateIndex < nextIndex; currentValidateIndex++) {
                        stepIDsForValidate.push(stepIDs[currentValidateIndex]);
                    }
                    const result = yield this.tryCompleteSteps(stepIDsForValidate);
                    if (!result.ok) {
                        if (result.firstFailedStepId !== this.currentStepId) {
                            yield this.switchStepById(result.firstFailedStepId);
                        }
                        return;
                    }
                }
                yield this.switchStepById(nextStepId);
            }
            catch (e) {
                this.internalDidCatchError(e);
            }
        });
    }
    didSelectNext() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const canLeave = yield this.canLeaveCurrentStep();
                if (!canLeave) {
                    return;
                }
                const ok = yield this.completeCurrentStep();
                if (ok) {
                    yield this.switchStepById(this.getNextOrPrevStepId(1));
                }
            }
            catch (e) {
                this.internalDidCatchError(e);
            }
        });
    }
    didSelectPrev() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const canLeave = yield this.canLeaveCurrentStep();
                if (!canLeave) {
                    return;
                }
                yield this.switchStepById(this.getNextOrPrevStepId(-1));
            }
            catch (e) {
                this.internalDidCatchError(e);
            }
        });
    }
    didSelectSkip() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.switchStepById(this.getNextOrPrevStepId(1));
            }
            catch (e) {
                this.internalDidCatchError(e);
            }
        });
    }
    didSelectDone() {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const canLeave = yield this.canLeaveCurrentStep();
                if (!canLeave) {
                    return;
                }
                const ok = yield this.completeCurrentStep();
                if (!ok) {
                    return;
                }
            }
            catch (e) {
                this.internalDidCatchError(e);
            }
            try {
                this.startLoading();
                const ok = (_c = (yield ((_b = (_a = this.listener).willDone) === null || _b === void 0 ? void 0 : _b.call(_a)))) !== null && _c !== void 0 ? _c : true;
                if (ok) {
                    (_e = (_d = this.listener).didDone) === null || _e === void 0 ? void 0 : _e.call(_d);
                    return;
                }
                this.cancelLoading();
            }
            catch (err) {
                this.internalDidCatchError(err);
                this.cancelLoading();
            }
        });
    }
    didSelectCancel() {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.startLoading();
                if ((_c = (yield ((_b = (_a = this.listener).willCancel) === null || _b === void 0 ? void 0 : _b.call(_a)))) !== null && _c !== void 0 ? _c : true) {
                    (_e = (_d = this.listener).didCancel) === null || _e === void 0 ? void 0 : _e.call(_d);
                }
            }
            catch (err) {
                this.internalDidCatchError(err);
            }
            finally {
                this.cancelLoading();
            }
        });
    }
    didEnterKeydown(ctrlKey = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const forceDone = ctrlKey;
            if (this.isLoading()) {
                return;
            }
            if (this.isActionDoneIsAvailable() && this.canDone(forceDone)) {
                yield this.didSelectDone();
                return;
            }
            if (!forceDone && this.isActionNextIsAvailable()) {
                yield this.didSelectNext();
            }
        });
    }
    startLoading() {
        this.loading++;
        this.updatePendingState();
    }
    cancelLoading() {
        this.loading--;
        this.updatePendingState();
    }
    startPending() {
        this.pending++;
        this.updatePendingState();
    }
    cancelPending() {
        this.pending--;
        this.updatePendingState();
    }
    didCatchError(err) {
        this.internalDidCatchError(err);
    }
    registerStepId(step) {
        this.stepRegistry[step.id] = step;
        if (step.unavailable) {
            this.stepsUnavailability[step.id] = true;
        }
    }
    updateStepAvailability(patchState) {
        return __awaiter(this, void 0, void 0, function* () {
            Object.entries(patchState).forEach(([stepId, value]) => {
                this.stepsUnavailability[stepId] = !value;
            });
            yield this.updateNavigation();
        });
    }
    updateStepDisableState(patchState) {
        return __awaiter(this, void 0, void 0, function* () {
            Object.entries(patchState).forEach(([stepId, value]) => {
                this.stepsDisableState[stepId] = value;
            });
            yield this.updateNavigation();
        });
    }
    switchStepById(id) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.currentStepId === id) {
                return;
            }
            try {
                const prevStepId = this.currentStepId;
                this.currentStepId = id;
                this.visitedIndex[id] = true;
                this.startLoading();
                yield ((_a = this.router) === null || _a === void 0 ? void 0 : _a.switchStepById(id));
                yield this.updateNavigation();
                (_c = (_b = this.config).onStepChange) === null || _c === void 0 ? void 0 : _c.call(_b, prevStepId || null, this.currentStepId);
            }
            catch (e) {
                this.internalDidCatchError(e);
            }
            finally {
                this.cancelLoading();
            }
        });
    }
    getActionsState() {
        return Object.assign({}, this.actionsState);
    }
    updateActionsState(patchState) {
        return __awaiter(this, void 0, void 0, function* () {
            this.actionsState = Object.assign(Object.assign({}, this.actionsState), patchState);
            return this.updateNavigation();
        });
    }
    updateHeaderState(state) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            (_b = (_a = this.presenter) === null || _a === void 0 ? void 0 : _a.presentHeader) === null || _b === void 0 ? void 0 : _b.call(_a, state);
        });
    }
    getCurrentStepId() {
        return this.currentStepId;
    }
    isLoading() {
        return this.loading > 0;
    }
    isPending() {
        return this.pending > 0;
    }
}
exports.WizardInteractor = WizardInteractor;
//# sourceMappingURL=WizardInteractor.js.map